import styled from '@emotion/styled';
import { memo, ReactNode, useState, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

import {
  white,
  blueGrayPale,
  gray04,
  black,
} from 'presentations/views/styles/color';
import {
  isMediaMax,
  mediaMax,
  spacing,
  zIndex,
} from 'presentations/views/styles';
import { ViewerItem } from 'presentations/api/schema/user';
import DashboardNavigation from 'presentations/views/components/organisms/DashboardNavigation';
import Progress from 'presentations/views/components/atoms/Progress';
const HeaderCalendarErrorMessage = dynamic(() =>
  import('presentations/views/components/organisms/HeaderCalendarErrorMessage'),
);
import { useChat } from 'react-live-chat-loader';
import {
  DashboardPortalContext,
  setTitle,
} from 'presentations/reducers/DashboardPortal';
import DashboardBottomNavigation from 'presentations/views/components/molecules/DashboardBottomNavigation';
import { isiOSApp as isiOSAppFunc } from 'presentations/views/helpers/electron';
import { apiPath } from 'presentations/routes/path';
import ChatPopover from 'presentations/views/components/organisms/ChatPopover';
import { AssistantWidgetContext } from 'presentations/reducers/AssistantWidget';
import { post } from 'infras/rest_client';

interface Props {
  title?: string;
  content?: ReactNode;
  isLoading?: boolean;
  viewer: ViewerItem;
  contentHeader?: ReactNode;
  mobileHeaderZIndex?: number;
  showHeaderCalendarError?: boolean;
  isMobileTabHidden?: boolean;
  isHideHeader?: boolean;
  onClickContainer?: any;
}

const Component = memo((props: Props) => {
  const isMobile = isMediaMax('tablet');
  const router = useRouter();
  const [isiOSApp, setIsiOSApp] = useState<boolean>(false);
  const { dispatch } = useContext(DashboardPortalContext);
  const assistantWidget = useContext(AssistantWidgetContext);
  const [state, loadChat] = useChat();

  // openSupportがクエリにあったらインターコムを開く
  useEffect(() => {
    if (router.isReady && Number(router.query.openSupport) === 1) {
      loadChat({ open: true });
    }
  }, [router.isReady, router.query.openSupport]);

  useEffect(() => {
    if (!props.title) return;
    dispatch(setTitle(props.title));
  }, [props.title]);

  useEffect(() => {
    setIsiOSApp(isiOSAppFunc());
  }, []);

  // iOSアプリからのイベントを処理
  useEffect(() => {
    if (!isiOSApp) return;

    // iOSアプリでrouter遷移させる
    const routerReplace = (event: CustomEvent) => {
      // /dashboardなど相対パスのみ許可
      if (typeof event.detail === 'string' && event.detail.startsWith('/')) {
        router.replace(event.detail);
      }
    };
    window.addEventListener<any>('jicooApp:routerReplace', routerReplace);

    const routerPush = (event: CustomEvent) => {
      // /dashboardなど相対パスのみ許可
      if (typeof event.detail === 'string' && event.detail.startsWith('/')) {
        router.push(event.detail);
      }
    };
    window.addEventListener<any>('jicooApp:routerPush', routerPush);

    // FCMトークンを送る
    const sendFcmToken = async (event: CustomEvent) => {
      if (typeof event.detail === 'string') {
        post(apiPath('/v1/user_notification_tokens/create'), {
          data: { deviceType: 2, token: event.detail },
        });
      }
    };
    window.addEventListener<any>('jicooApp:sendFcmToken', sendFcmToken);

    return () => {
      window.removeEventListener<any>('jicooApp:routerReplace', routerReplace);
      window.removeEventListener<any>('jicooApp:routerPush', routerPush);
      window.removeEventListener<any>('jicooApp:sendFcmToken', sendFcmToken);
    };
  }, [isiOSApp]);

  return (
    <>
      <Container>
        <HeaderContainer data-intercom-target="main-header">
          <DashboardNavigation
            viewer={props.viewer}
            isShowMobileCreateMenu={!props.isMobileTabHidden}
            mobileZIndex={props.mobileHeaderZIndex}
          />
        </HeaderContainer>
        <Content data-intercom-target="main-container">
          {props.showHeaderCalendarError && <HeaderCalendarErrorMessage />}
          {props.contentHeader && (
            <TabBarContentHeader>{props.contentHeader}</TabBarContentHeader>
          )}
          {props.isLoading ? (
            <ProgressContainer>
              <Progress />
            </ProgressContainer>
          ) : (
            <TabBarContent>{props.content}</TabBarContent>
          )}
        </Content>
      </Container>
      {isMobile && !props.isMobileTabHidden && !isiOSApp && (
        <DashboardBottomNavigation />
      )}
      {assistantWidget.state.chatPopoverAnchor && (
        <ChatPopover
          anchor={assistantWidget.state.chatPopoverAnchor}
          viewer={props.viewer}
        />
      )}
    </>
  );
});

export const Container = styled.div<{ isHideHeader?: boolean }>`
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  /* flex-direction: column; */
  ${mediaMax.tablet} {
    padding-bottom: ${(props) => !props.isHideHeader && 'calc(56px)'};
  }
`;
export const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex(1100)};
  background-color: ${white};
  border-bottom: 1px solid ${gray04};
  ${mediaMax.tablet} {
    display: none;
  }
`;
export const MeetingWidgetContainer = styled.div`
  z-index: ${zIndex(1500)};
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
`;

const Content = styled.div`
  background-color: ${blueGrayPale};
  flex: 1;
  ${mediaMax.tablet} {
    width: 100%;
    padding-bottom: 42px;
  }
`;
const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${spacing(128)};
`;
const TabBarContentHeader = styled.div`
  margin-bottom: ${spacing(48)};
  background-color: ${white};
  padding: 0 ${spacing(48)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
    padding: 0 ${spacing(16)};
  }
`;
const TabBarContent = styled.div`
  padding: 0 ${spacing(48)};
  ${mediaMax.tablet} {
    width: 100%;
    padding: 0 ${spacing(16)};
    /* padding-bottom: 42px; */
    padding-bottom: 56px;
  }
`;
export default Component;
